import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import {
  useVoiceCall,
  type UseVoiceCallOptions,
  type VoiceCallControls,
  type VoiceCallFailure,
} from './useVoiceCall';

/** {@link useVoiceCall} with its failures and idle hang-ups reported as toasts. */
export function useChatVoiceCall(
  onDelegate: UseVoiceCallOptions['onDelegate'],
  onVoiceTurn: UseVoiceCallOptions['onVoiceTurn'],
  active: boolean,
): VoiceCallControls {
  const gt = useGT();
  const { showToast } = useToast();

  const failureMessage = (reason: VoiceCallFailure): string => {
    switch (reason) {
      case 'microphone':
        return gt(
          'Could not access the microphone. Check your browser permissions.',
          { context: 'toast when a voice call cannot open the microphone' },
        );
      case 'disconnected':
        return gt('The voice call was disconnected.', {
          context: 'toast when a live voice call with the assistant drops',
        });
      case 'start-failed':
        return gt('Could not start the voice call.', {
          context: 'toast when a voice call with the assistant fails to start',
        });
      case 'idle':
        return gt('The voice call ended because nobody spoke.', {
          context:
            'toast when a voice call with the assistant hangs up because nobody spoke',
        });
    }
  };

  return useVoiceCall({
    onDelegate,
    onVoiceTurn,
    active,
    onError: (reason) =>
      showToast({
        title: gt('Voice Call', {
          context: 'toast title for voice call errors in the chat panel',
        }),
        message: failureMessage(reason),
        // Hanging up a quiet call is expected, not a fault.
        color: reason === 'idle' ? 'info' : 'error',
      }),
  });
}
